// @flow
import React from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { get, includes, isNaN, toNumber, without } from 'lodash';
import { Icon, Button, Modal } from '@elements';
import { fsPrintf, isEnterKey, keepKeysRef, UserNotifications } from '@helpers/helpers';
import type { FormElementDataString } from '@types';


type Props = {
  recurrent: Object,
  setModal: Function,
  onChange: Function,
  isNew: boolean,
  isBlockedAppointment?: boolean,
  onDeleteRecurrentEvents: Function,
  appointmentDate: string,
  _: Function,
};

type Choice = 'none' | 'weekly' | 'biweekly';

const CHOICES: Array<Choice> = ['none', 'weekly', 'biweekly'];
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const MIN_COUNT = 2;
const MAX_COUNT = 99;

const ARROW_STEPS = { 37: -1, 38: -1, 39: 1, 40: 1 };

const weekdayOf = (date: Object): string => WEEKDAYS[date.isoWeekday() - 1];
// A number the field keeps once left: a whole one from 2 to 99, not the text typed in it
const isKeptCount = (count: any): boolean => typeof count === 'number' && count >= MIN_COUNT && count <= MAX_COUNT;
// The form's date: "2026-10-29 14:20:31" as the agenda opens it, "2026-10-29" once picked
const dayOf = (date: ?string): Object => moment(date, 'YYYY-MM-DD');

// The first and last dates of the series the API creates (AppointmentGroup#create_appointments): `count` dates from
// the appointment's own, on each ticked weekday, or on its weekday every 2 weeks. When its weekday isn't ticked, the
// appointment itself moves to the first ticked day after it.
export const seriesRange = (date: ?string, mode: string, days: Array<string>, count: number): ?{ first: Object, last: Object } => {
  const start = dayOf(date);
  if (!start.isValid() || !(count >= MIN_COUNT)) return null;
  if (mode === 'biweekly') return { first: start, last: start.clone().add(14 * (count - 1), 'days') };
  const on = days.filter(day => includes(WEEKDAYS, day));
  if (on.length === 0) return null;

  const day = start.clone();
  let first = null;
  let found = 0;
  while (found < count) {
    if (includes(on, weekdayOf(day))) {
      found += 1;
      first = first || day.clone();
    }
    if (found < count) day.add(1, 'day');
  }
  return { first, last: day };
};

export default class RecurrencyModal extends React.Component<Props> {
  // The sub-screen can go while the number field still holds what was typed ("1", "12"), with no blur on the way: a
  // tap on a button doesn't take the focus on iOS, and a removed field never blurs. Keep it as leaving the field does
  componentWillUnmount() {
    const { recurrent, isNew } = this.props;
    const appointmentsCount = get(recurrent, 'appointmentsCount');
    if (isNew && get(recurrent, 'recurrentEnabled') && !isKeptCount(appointmentsCount)) {
      this.onAppointmentsCountBurl({ name: 'appointmentsCount', value: appointmentsCount });
    }
  }

  // A new appointment: does it repeat, and how. The options are exclusive, so one list with one choice
  renderChoices() {
    const { recurrent, appointmentDate, _ } = this.props;
    const { recurrentEnabled, mode } = recurrent || {};
    const chosen: Choice = recurrentEnabled && includes(['weekly', 'biweekly'], mode) ? mode : 'none';

    const date = dayOf(appointmentDate);
    const dayName = date.isValid() ? _(`weekday/${weekdayOf(date)}`, 'constants') : '';

    const labels = {
      none: { label: _('label/recurrency/none') },
      weekly: { label: _('label/recurrency/weekly'), hint: chosen !== 'weekly' ? _('label/recurrency/weekly_hint') : null },
      // The API repeats it on the appointment's own weekday: "Quinta sim, quinta não"
      biweekly: { label: _('label/recurrency/biweekly'), hint: dayName ? _('label/recurrency/biweekly_hint', null, fsPrintf(dayName, dayName.toLowerCase())) : null },
    };
    const choices = CHOICES.map(value => ({ value, ...labels[value] }));

    // A radio group: Tab reaches the chosen option only, the arrows move the choice
    return (
      <ul className="recurrencyChoices" role="radiogroup" aria-label={_('label/recurrency/recurrent_appointment')}>
        {choices.map((choice, index) => (
          <li key={choice.value} role="none" className={classnames('recurrencyChoice', { chosen: chosen === choice.value })}>
            <button
              type="button"
              role="radio"
              aria-checked={chosen === choice.value}
              tabIndex={chosen === choice.value ? 0 : -1}
              className="recurrencyChoice-row"
              onClick={this.onChoose(choice.value)}
              onKeyDown={this.onChoiceKey(index)}
            >
              <span className="recurrencyChoice-radio" />
              <span className="recurrencyChoice-text">
                <span className="recurrencyChoice-label">{choice.label}</span>
                {choice.hint && <span className="recurrencyChoice-hint">{choice.hint}</span>}
              </span>
            </button>
            {chosen === choice.value && choice.value !== 'none' && this.renderDetails(choice.value)}
          </li>
        ))}
      </ul>
    );
  }

  // Under the chosen option: the weekdays (weekly), how many in all, and what that gives
  renderDetails(mode: Choice) {
    const { recurrent, appointmentDate, isBlockedAppointment, _ } = this.props;
    const recurrentDays = get(recurrent, 'recurrentDays') || [];
    const appointmentsCount = get(recurrent, 'appointmentsCount', '');
    const count = this.currentCount();

    const start = dayOf(appointmentDate);
    const range = seriesRange(appointmentDate, mode, recurrentDays, toNumber(appointmentsCount));
    // The year only when the series goes into another one
    const showYear = range && !range.last.isSame(start, 'year');
    const format = (date: Object) => date.format(showYear ? 'DD/MM/YYYY' : 'DD/MM');
    const moved = range && !range.first.isSame(start, 'day');

    let summary = null;
    if (mode === 'weekly' && recurrentDays.length === 0) {
      summary = <p className="recurrencySummary error">{_('label/recurrency/pick_a_day')}</p>;
    } else if (range) {
      summary = (
        <p className="recurrencySummary">
          {_(isBlockedAppointment ? 'label/recurrency/summary_blocked' : 'label/recurrency/summary', null,
            fsPrintf(toNumber(appointmentsCount), format(range.first), format(range.last)))}
          {moved && ` ${_('label/recurrency/starts_later', null, fsPrintf(format(start)))}`}
        </p>
      );
    }

    return (
      <div className="recurrencyDetails" role="group" aria-label={_(`label/recurrency/${mode}`)} onKeyDown={this.onDetailsKey}>
        {mode === 'weekly' && (
          <div className="recurrencyDays" role="group" aria-label={_('label/recurrency/days')}>
            {WEEKDAYS.map(day => (
              <button
                type="button"
                key={day}
                className={classnames('recurrencyDay', { checked: includes(recurrentDays, day) })}
                aria-pressed={includes(recurrentDays, day)}
                aria-label={_(`weekday/${day}`, 'constants')}
                onClick={this.onToggleDay(day)}
              >
                {_(`label/recurrency/short/${day}`)}
              </button>
            ))}
          </div>
        )}
        <div className="recurrencyCount">
          <label htmlFor="recurrencyCountInput">
            {_(isBlockedAppointment ? 'label/recurrency/count_blocked' : 'label/recurrency/count')}
          </label>
          <div className="recurrencyStepper">
            {/* aria-disabled, not disabled: a disabled button would drop the focus, and the next Enter would save */}
            <button type="button" aria-label={_('action/recurrency/fewer')} aria-disabled={count <= MIN_COUNT} onClick={this.onStep(-1)}>−</button>
            <input
              id="recurrencyCountInput"
              name="appointmentsCount"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={appointmentsCount}
              onChange={this.onCountInput(this.onAppointmentsCountChange)}
              onBlur={this.onCountInput(this.onAppointmentsCountBurl)}
              onFocus={(event) => event.target.select()}
              onKeyDown={this.onCountKey}
            />
            <button type="button" aria-label={_('action/recurrency/more')} aria-disabled={count >= MAX_COUNT} onClick={this.onStep(1)}>+</button>
          </div>
        </div>
        {summary}
      </div>
    );
  }

  // An appointment already saved in a series: how many it has, and deleting it with the ones after it
  renderSeries() {
    const { recurrent, isBlockedAppointment, _ } = this.props;
    if (!get(recurrent, 'recurrentEnabled')) return null;

    return (
      <div className="recurrencySeries">
        <p className="recurrencySummary">
          {_(isBlockedAppointment ? 'label/recurrency/series_blocked' : 'label/recurrency/series', null,
            fsPrintf(get(recurrent, 'appointmentsCount')))}
        </p>
        <Button className="recurrencyDelete" onClick={this.handleDeleteSeries}>
          <Icon iconName="delete" size={20} />
          <span>{_(isBlockedAppointment ? 'label/recurrency/delete_all_future_blocked' : 'label/recurrency/delete_all_future_events')}</span>
        </Button>
      </div>
    );
  }

  render() {
    const { isNew, _ } = this.props;

    return (
      <Modal id="recurringModal" className="secondaryModal">
        <div className="modalHeader actionHeader">
          <Button className="backButton" onClick={this.handleBack}>
            <Icon iconName="arrow-left" className="icon" />
          </Button>
          <span className="modalTitle">{_('label/recurrency/recurrent_appointment')}</span>
        </div>
        <div className="modalGrid">
          <div className="modalContainer">
            <div className="modalContent" ref={this.bindKeys}>
              {isNew ? this.renderChoices() : this.renderSeries()}
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  // Enter on a button presses it instead of saving the appointment (AppointmentModalForm.handleKeyDown)
  bindKeys = keepKeysRef(event => isEnterKey(event) && get(event, ['target', 'tagName']) === 'BUTTON');

  currentCount = (): number => {
    const appointmentsCount = get(this.props.recurrent, 'appointmentsCount', '');
    const count = toNumber(appointmentsCount);
    return appointmentsCount === '' || isNaN(count) ? MIN_COUNT : count;
  };

  // ↑ ↓ ← → on an option move the choice, and don't reach the form, where they change the menu row
  // (AppointmentModalForm.handleKeyDown, on document)
  onChoiceKey = (index: number) => (event: Object) => {
    const step = ARROW_STEPS[event.keyCode];
    if (!step) return;
    event.preventDefault();
    event.stopPropagation();
    const next = (index + step + CHOICES.length) % CHOICES.length;
    const radios = event.currentTarget.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]');
    if (radios[next]) radios[next].focus();
    this.onChoose(CHOICES[next])();
  };

  // ↑ ↓ in the fields under the option stay here too; in the number field they step it, as − and + do
  onDetailsKey = (event: Object) => {
    if (event.keyCode !== 38 && event.keyCode !== 40) return;
    event.stopPropagation();
    if (get(event, ['target', 'id']) === 'recurrencyCountInput') {
      event.preventDefault();
      this.onStep(event.keyCode === 38 ? 1 : -1)();
    }
  };

  onChoose = (choice: Choice) => () => {
    const { recurrent } = this.props;
    const { recurrentEnabled, mode } = recurrent || {};
    if (choice === 'none') {
      if (recurrentEnabled) this.clearRecurrent();
    } else if (!recurrentEnabled || mode !== choice) {
      this.setRecurrent(choice, this.appointmentWeekdays());
    }
  };

  // The appointment's own weekday, ticked when it starts repeating (and the one the API needs for every 2 weeks). It
  // was counted in moment's locale week: a Sunday ticked none in pt-BR, so an every-2-weeks series on a Sunday was
  // refused by the API (AppointmentGroup needs a weekday), and Spanish, whose week starts on Monday, ticked the day before
  appointmentWeekdays = (): Array<string> => {
    const date = dayOf(this.props.appointmentDate);
    return date.isValid() ? [weekdayOf(date)] : [];
  };

  onToggleDay = (day: string) => () => {
    const recurrentDays = get(this.props.recurrent, 'recurrentDays') || [];
    this.onChangeRecurrentDays(includes(recurrentDays, day) ? without(recurrentDays, day) : [...recurrentDays, day]);
  };

  onStep = (delta: number) => () => {
    const appointmentsCount = Math.min(MAX_COUNT, Math.max(MIN_COUNT, this.currentCount() + delta));
    if (appointmentsCount !== this.currentCount()) this.patchRecurrent({ appointmentsCount });
  };

  onCountInput = (handler: Function) => (event: Object) => handler({ name: 'appointmentsCount', value: event.target.value });

  // Enter saves the appointment (AppointmentModalForm.handleKeyDown): the field is left first, so the number saved is
  // the one leaving it keeps (2 to 99), not the one typed
  onCountKey = (event: Object) => {
    if (isEnterKey(event)) event.target.blur();
  };

  onAppointmentsCountChange = (data: FormElementDataString) => {
    let appointmentsCount = String(data.value).replace(/\D+/g, '');
    appointmentsCount = String(appointmentsCount).trim().length > 0 ? toNumber(appointmentsCount) : '';
    appointmentsCount = String(appointmentsCount).substring(0, 2);
    this.patchRecurrent({ appointmentsCount });
  };

  onAppointmentsCountBurl = (data: FormElementDataString) => {
    let appointmentsCount = String(data.value).replace(/\D+/g, '');
    appointmentsCount = String(appointmentsCount).trim().length > 0 ? toNumber(appointmentsCount) : '';
    appointmentsCount = String(appointmentsCount).substring(0, 2);
    if (!isNaN(appointmentsCount)) {
      appointmentsCount = toNumber(appointmentsCount);
      appointmentsCount = appointmentsCount < MIN_COUNT ? MIN_COUNT : appointmentsCount;
      appointmentsCount = appointmentsCount > MAX_COUNT ? MAX_COUNT : appointmentsCount;
      appointmentsCount = isNaN(appointmentsCount) ? MIN_COUNT : appointmentsCount;
    }
    this.patchRecurrent({ appointmentsCount });
  }

  setRecurrent = (mode: 'weekly' | 'biweekly', recurrentDays: Array<any>) => {
    this.props.onChange({
      name: 'recurrent',
      value: {
        appointmentsCount: 10,
        ...this.props.recurrent,
        recurrentEnabled: true,
        mode,
        recurrentDays,
      },
    });
  };

  clearRecurrent = () => {
    this.props.onChange({
      name: 'recurrent',
      value: null,
    });
  };

  onChangeRecurrentDays = (recurrentDays: Array<string>) => this.patchRecurrent({ recurrentDays });

  patchRecurrent = (fields: Object) => {
    this.props.onChange({
      name: 'recurrent',
      value: {
        ...this.props.recurrent,
        ...fields,
      },
    });
  };

  // Deletes this one and the later ones of the series at once: asks first, as the form's own delete does
  handleDeleteSeries = () => {
    const alert = this.props.isBlockedAppointment ? 'alert/delete/recurrent_blocked' : 'alert/delete/recurrent_appointments';
    UserNotifications.confirmI18n(alert, 'warning', '#FFA433', true).then((confirmed) => {
      if (confirmed) this.props.onDeleteRecurrentEvents();
    });
  };

  handleBack = () => this.props.setModal(null);
}
